import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Control,
  Controller,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { MdContentCopy, MdDeleteOutline } from 'react-icons/md';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Textarea } from '../../tailwindComponents/Textarea';
import { MultiSelect } from '../../tailwindComponents/MultiSelect';
import { ProductSkusFormValues } from './types';
import { DIMENSION_UNITS, SKU_UNITS, WEIGHT_UNITS } from './constants';
import { FieldZone } from './FieldZone';
import { ImageField } from './ImageField';
import { PalletisingSection } from './PalletisingSection';
import { derivePalletProductsSuggestion } from './skuDerivation';
import { skuPackDescription, skuUnitNoun } from '../../utils/sku';

const countLeafErrors = (node: unknown): number => {
  if (!node || typeof node !== 'object') return 0;
  const record = node as Record<string, unknown>;
  if (typeof record.message === 'string') return 1;
  return Object.values(record).reduce<number>(
    (total, child) => total + countLeafErrors(child),
    0
  );
};

export function SkuCard({
  index,
  control,
  formState,
  setValue,
  getValues,
  mode,
  supplierOptions,
  onOpenCreateSupplier,
  onDuplicate,
  onRemove,
  canRemove,
  collapsed,
  onToggleCollapsed,
}: {
  index: number;
  control: Control<ProductSkusFormValues>;
  formState: UseFormStateReturn<ProductSkusFormValues>;
  setValue: UseFormSetValue<ProductSkusFormValues>;
  getValues: UseFormGetValues<ProductSkusFormValues>;
  mode: 'create' | 'edit';
  supplierOptions: { label: string; value: string }[];
  onOpenCreateSupplier: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  canRemove: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const cardErrors = formState.errors.skus?.[index];
  const errorCount = countLeafErrors(cardErrors);

  const unitOfMeasure = useWatch({
    control,
    name: `skus.${index}.unitOfMeasure`,
  });
  const isItemUnit = unitOfMeasure?.value === 'Item';
  const isPalletUnit = unitOfMeasure?.value === 'Pallet';
  const amountOfProductInSku = useWatch({
    control,
    name: `skus.${index}.amountOfProductInSku`,
  });
  const productsPerSkuValue = Number(amountOfProductInSku);
  const showProductsPreview =
    !isItemUnit &&
    Number.isFinite(productsPerSkuValue) &&
    productsPerSkuValue > 0;
  const productUnitName = skuUnitNoun(
    { unitOfMeasure: unitOfMeasure?.value },
    'unit'
  );
  const image = useWatch({ control, name: `skus.${index}.image` });
  const barcodeImage = useWatch({
    control,
    name: `skus.${index}.barcodeImage`,
  });
  const skuImage = useWatch({ control, name: `skus.${index}.skuImage` });
  const skuBarcodeImage = useWatch({
    control,
    name: `skus.${index}.skuBarcodeImage`,
  });
  const skuName = useWatch({ control, name: `skus.${index}.name` });
  const skuId = useWatch({ control, name: `skus.${index}.skuId` });
  const grossWeight = useWatch({ control, name: `skus.${index}.grossWeight` });
  const grossWeightUnit = useWatch({
    control,
    name: `skus.${index}.grossWeightUnitOfMeasure`,
  });
  const pallets =
    useWatch({ control, name: `skus.${index}.palletising` }) ?? [];
  const allCards = useWatch({ control, name: 'skus' }) ?? [];
  const productName = useWatch({ control, name: 'productName' });

  const {
    fields: customFieldRows,
    append: appendCustomField,
    remove: removeCustomField,
  } = useFieldArray({ control, name: `skus.${index}.customFields` });

  const [showDescription, setShowDescription] = useState(() =>
    Boolean(getValues(`skus.${index}.description`))
  );

  const cardTitle = skuName || skuId || `SKU ${index + 1}`;
  const packSummary = skuPackDescription({
    unitOfMeasure: unitOfMeasure?.value,
    amountOfProductInSku: productsPerSkuValue || null,
  });

  const packQualifier =
    isItemUnit || !(productsPerSkuValue > 1) ? '' : ` — ${packSummary}`;
  const derivedName = productName ? `${productName}${packQualifier}` : '';
  const previousDerivedName = useRef(derivedName);
  useEffect(() => {
    if (mode !== 'edit') {
      const currentName = getValues(`skus.${index}.name`);
      if (currentName === '' || currentName === previousDerivedName.current) {
        setValue(`skus.${index}.name`, derivedName);
      }
    }
    previousDerivedName.current = derivedName;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedName]);

  const palletSuggestion = isPalletUnit
    ? derivePalletProductsSuggestion(allCards, index)
    : null;
  const showPalletSuggestion = Boolean(
    palletSuggestion &&
      Number(amountOfProductInSku) !== palletSuggestion.products
  );

  const firstPallet = pallets[0];
  const firstPalletUnits = firstPallet
    ? (Number(firstPallet.amountOfSkusInLayer) || 0) *
      (Number(firstPallet.amountOfLayersInPallet) || 0)
    : 0;
  const palletSummary =
    firstPallet?.type && firstPalletUnits > 0
      ? `${firstPallet.type.replace(
          ' Pallet',
          ''
        )} · ${firstPalletUnits}/pallet${
          pallets.length > 1 ? ` +${pallets.length - 1}` : ''
        }`
      : null;
  const weightSummary =
    grossWeight && Number(grossWeight) > 0
      ? `${grossWeight} ${grossWeightUnit?.value ?? ''}`.trim()
      : null;

  const collapsedSummary = [skuId, packSummary, weightSummary, palletSummary]
    .filter(Boolean)
    .join(' · ');
  const expandedSubtitle = skuId ? `${skuId} · ${packSummary}` : packSummary;

  const canToggle = mode === 'create';

  const headerContent = (
    <>
      {canToggle && (
        <ChevronDownIcon
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
            collapsed ? '-rotate-90' : ''
          }`}
        />
      )}
      <div className='min-w-0'>
        {collapsed ? (
          <span className='block truncate font-inter text-sm text-gray-900'>
            <span className='font-semibold'>{cardTitle}</span>
            {collapsedSummary && (
              <span className='ml-2 text-gray-500'>{collapsedSummary}</span>
            )}
          </span>
        ) : (
          <>
            <span className='block truncate font-inter text-base font-semibold text-gray-900'>
              {cardTitle}
            </span>
            <span className='block truncate font-inter text-sm text-gray-500'>
              {expandedSubtitle}
            </span>
          </>
        )}
      </div>
    </>
  );

  return (
    <section
      data-sku-card={index}
      className={
        collapsed
          ? 'scroll-mt-20 rounded-md border border-gray-200 bg-white'
          : 'scroll-mt-20 rounded-lg border border-gray-200 bg-white shadow-sm'
      }
    >
      <div
        className={`flex items-center justify-between gap-3 ${
          collapsed ? 'px-4 py-2.5' : 'p-4'
        }`}
      >
        {canToggle ? (
          <button
            type='button'
            onClick={onToggleCollapsed}
            className='flex min-w-0 flex-1 items-center gap-3 text-left'
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${cardTitle}`}
          >
            {headerContent}
          </button>
        ) : (
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            {headerContent}
          </div>
        )}
        {errorCount > 0 && (
          <span className='shrink-0 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700'>
            {errorCount} issue{errorCount === 1 ? '' : 's'}
          </span>
        )}
        {mode === 'create' && (
          <div className='flex shrink-0 items-center gap-1'>
            <button
              type='button'
              onClick={onDuplicate}
              title='Duplicate'
              className='rounded-md p-1.5 text-gray-400 transition hover:bg-gray-50 hover:text-gray-700'
              aria-label={`Duplicate ${cardTitle}`}
            >
              <MdContentCopy size={16} />
            </button>
            {canRemove && (
              <button
                type='button'
                onClick={onRemove}
                title='Remove'
                className='rounded-md p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600'
                aria-label={`Remove ${cardTitle}`}
              >
                <MdDeleteOutline size={18} />
              </button>
            )}
          </div>
        )}
      </div>
      <div
        className={
          collapsed ? 'hidden' : 'space-y-4 border-t border-gray-100 p-5'
        }
      >
        <div className='flex flex-col gap-4 xl:flex-row'>
          <div className='min-w-0 flex-1'>
            <FieldZone
              title='SKU details'
              hint='What this SKU is and how it converts to products.'
            >
              <div className='flex flex-wrap gap-4'>
                <div className='w-56'>
                  <Controller
                    name={`skus.${index}.skuId`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label='ID'
                        placeholder='e.g. COKE-330-CS12'
                        isInvalid={!!cardErrors?.skuId}
                        validationMessage={cardErrors?.skuId?.message as string}
                      />
                    )}
                  />
                </div>
                <div className='min-w-[12rem] flex-1'>
                  <Controller
                    name={`skus.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label='Name'
                        placeholder='e.g. Coke Can 330ml 12-pack'
                        isInvalid={!!cardErrors?.name}
                        validationMessage={cardErrors?.name?.message as string}
                      />
                    )}
                  />
                </div>
              </div>
              <div className='flex flex-wrap gap-4'>
                <div className='w-40'>
                  <Controller
                    name={`skus.${index}.unitOfMeasure`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        label='Unit of Measure'
                        options={SKU_UNITS}
                        selected={field.value}
                        setSelected={(value) => {
                          const wasItemUnit = field.value?.value === 'Item';
                          field.onChange(value);
                          if (value?.value === 'Item') {
                            setValue(
                              `skus.${index}.amountOfProductInSku`,
                              '1',
                              {
                                shouldValidate: true,
                              }
                            );
                          } else if (
                            wasItemUnit &&
                            getValues(`skus.${index}.amountOfProductInSku`) ===
                              '1'
                          ) {
                            setValue(`skus.${index}.amountOfProductInSku`, '');
                          }
                          if (value?.value === 'Pallet') {
                            const currentPallets =
                              getValues(`skus.${index}.palletising`) ?? [];
                            currentPallets.forEach((_, palletIndex) => {
                              setValue(
                                `skus.${index}.palletising.${palletIndex}.amountOfSkusInLayer`,
                                '1'
                              );
                              setValue(
                                `skus.${index}.palletising.${palletIndex}.amountOfLayersInPallet`,
                                '1'
                              );
                            });
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <div className='w-44'>
                  <Controller
                    name={`skus.${index}.amountOfProductInSku`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label={`Products per ${
                          productUnitName === 'unit' ? 'unit' : productUnitName
                        }`}
                        placeholder='e.g. 12'
                        disabled={isItemUnit}
                        isInvalid={!!cardErrors?.amountOfProductInSku}
                        validationMessage={
                          cardErrors?.amountOfProductInSku?.message as string
                        }
                      />
                    )}
                  />
                </div>
                <div className='w-64'>
                  <Controller
                    name={`skus.${index}.barcode`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        label='SKU Barcode'
                        placeholder='e.g. 5000112637922'
                        {...field}
                        isInvalid={!!cardErrors?.barcode}
                        validationMessage={
                          cardErrors?.barcode?.message as string
                        }
                        optional
                      />
                    )}
                  />
                </div>
                <div className='w-48'>
                  <Controller
                    name={`skus.${index}.lowStockThreshold`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        label='Low stock alert threshold'
                        placeholder='e.g. 500'
                        helperText='In product units.'
                        isInvalid={!!cardErrors?.lowStockThreshold}
                        validationMessage={
                          cardErrors?.lowStockThreshold?.message as string
                        }
                        optional
                      />
                    )}
                  />
                </div>
              </div>
              {(showProductsPreview || isItemUnit) && (
                <div className='rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-700'>
                  {isItemUnit ? (
                    <>
                      An <span className='font-medium'>Item</span> SKU is a
                      single product — stocked individually.
                    </>
                  ) : (
                    <>
                      1 {productUnitName} ={' '}
                      <span className='font-semibold'>
                        {productsPerSkuValue.toLocaleString()} product
                        {productsPerSkuValue === 1 ? '' : 's'}
                      </span>
                      {productsPerSkuValue > 1000 && (
                        <span className='ml-2 text-amber-700'>
                          — unusually high; check this is per {productUnitName},
                          not total stock.
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
              {showPalletSuggestion && palletSuggestion && (
                <div className='flex flex-wrap items-center justify-between gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-700'>
                  <span>
                    Suggested:{' '}
                    <span className='font-semibold'>
                      {palletSuggestion.products.toLocaleString()} products
                    </span>{' '}
                    per pallet — {palletSuggestion.unitsPerPallet}{' '}
                    {palletSuggestion.sourceUnitNoun}s ×{' '}
                    {palletSuggestion.productsPerUnit} (from{' '}
                    {palletSuggestion.sourceLabel})
                  </span>
                  <button
                    type='button'
                    onClick={() =>
                      setValue(
                        `skus.${index}.amountOfProductInSku`,
                        String(palletSuggestion.products),
                        { shouldValidate: true }
                      )
                    }
                    className='shrink-0 font-inter text-sm font-medium text-primary hover:underline'
                  >
                    Apply
                  </button>
                </div>
              )}
              <div className='max-w-md'>
                <Controller
                  name={`skus.${index}.suppliers`}
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      label='Suppliers'
                      options={supplierOptions}
                      selected={field.value || []}
                      setSelected={field.onChange}
                      actionLabel='+ Create New Supplier'
                      onActionClick={onOpenCreateSupplier}
                    />
                  )}
                />
              </div>
              {showDescription ? (
                <Controller
                  name={`skus.${index}.description`}
                  control={control}
                  render={({ field }) => (
                    <Textarea {...field} label='Description' optional />
                  )}
                />
              ) : (
                <div>
                  <button
                    type='button'
                    onClick={() => setShowDescription(true)}
                    className='font-inter text-sm font-medium text-primary hover:underline'
                  >
                    + Add description
                  </button>
                </div>
              )}
              {customFieldRows.length === 0 ? (
                <div>
                  <button
                    type='button'
                    onClick={() =>
                      appendCustomField({
                        id: uuidv4(),
                        type: 'Text',
                        label: '',
                        value: '',
                      })
                    }
                    className='font-inter text-sm font-medium text-primary hover:underline'
                  >
                    + Add additional fields (commodity codes, finish…)
                  </button>
                </div>
              ) : (
                <div className='space-y-3'>
                  {customFieldRows.map((customFieldRow, rowIndex) => (
                    <div
                      key={customFieldRow.id}
                      className='flex flex-wrap items-start gap-3'
                    >
                      <div className='w-56'>
                        <Controller
                          name={`skus.${index}.customFields.${rowIndex}.label`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label={rowIndex === 0 ? 'Field name' : undefined}
                              aria-label='Field name'
                              placeholder='e.g. Commodity code (import)'
                            />
                          )}
                        />
                      </div>
                      <div className='min-w-[12rem] flex-1'>
                        <Controller
                          name={`skus.${index}.customFields.${rowIndex}.value`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label={rowIndex === 0 ? 'Value' : undefined}
                              aria-label='Value'
                              placeholder='e.g. 94051990'
                            />
                          )}
                        />
                      </div>
                      <button
                        type='button'
                        onClick={() => removeCustomField(rowIndex)}
                        aria-label='Remove additional field'
                        className={`shrink-0 rounded-md p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600 ${
                          rowIndex === 0 ? 'mt-7' : 'mt-1'
                        }`}
                      >
                        <MdDeleteOutline size={18} />
                      </button>
                    </div>
                  ))}
                  <button
                    type='button'
                    onClick={() =>
                      appendCustomField({
                        id: uuidv4(),
                        type: 'Text',
                        label: '',
                        value: '',
                      })
                    }
                    className='font-inter text-sm font-medium text-primary hover:underline'
                  >
                    + Add another field
                  </button>
                </div>
              )}
            </FieldZone>
          </div>
          <div className='xl:w-[320px] xl:shrink-0'>
            <FieldZone title='Images'>
              <ImageField
                label='SKU image'
                dropzoneId={`sku-image-dropzone-${index}`}
                squareCrop
                compact
                existingImage={image}
                stagedFile={skuImage}
                onStage={(file) => setValue(`skus.${index}.skuImage`, file)}
                onRemoveExisting={(id) => {
                  setValue(`skus.${index}.deletedSKUImageId`, id);
                  setValue(`skus.${index}.image`, null);
                }}
              />
              <ImageField
                label='SKU barcode image'
                dropzoneId={`sku-barcode-dropzone-${index}`}
                compact
                existingImage={barcodeImage}
                stagedFile={skuBarcodeImage}
                onStage={(file) =>
                  setValue(`skus.${index}.skuBarcodeImage`, file)
                }
                onRemoveExisting={(id) => {
                  setValue(`skus.${index}.deletedBarcodeImageId`, id);
                  setValue(`skus.${index}.barcodeImage`, null);
                }}
              />
            </FieldZone>
          </div>
        </div>
        <FieldZone
          title='Unit dimensions & weight'
          hint={`The size and weight of one ${
            productUnitName === 'unit' ? 'unit' : productUnitName
          } — used by warehouse handlers.`}
        >
          <div className='flex flex-wrap gap-4'>
            <div className='w-24'>
              <Controller
                name={`skus.${index}.dimensions.dimensionsUnitOfMeasure`}
                control={control}
                render={({ field }) => (
                  <Select
                    label='Unit'
                    options={DIMENSION_UNITS}
                    selected={field.value}
                    setSelected={field.onChange}
                  />
                )}
              />
            </div>
            <div className='w-28'>
              <Controller
                name={`skus.${index}.dimensions.length`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Length'
                    isInvalid={!!cardErrors?.dimensions?.length}
                    validationMessage={
                      cardErrors?.dimensions?.length?.message as string
                    }
                  />
                )}
              />
            </div>
            <div className='w-28'>
              <Controller
                name={`skus.${index}.dimensions.width`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Width'
                    isInvalid={!!cardErrors?.dimensions?.width}
                    validationMessage={
                      cardErrors?.dimensions?.width?.message as string
                    }
                  />
                )}
              />
            </div>
            <div className='w-28'>
              <Controller
                name={`skus.${index}.dimensions.height`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Height'
                    isInvalid={!!cardErrors?.dimensions?.height}
                    validationMessage={
                      cardErrors?.dimensions?.height?.message as string
                    }
                  />
                )}
              />
            </div>
            <div className='hidden w-px self-stretch bg-gray-200 md:block' />
            <div className='w-28'>
              <Controller
                name={`skus.${index}.grossWeightUnitOfMeasure`}
                control={control}
                render={({ field }) => (
                  <Select
                    label='Weight unit'
                    options={WEIGHT_UNITS}
                    selected={field.value}
                    setSelected={field.onChange}
                  />
                )}
              />
            </div>
            <div className='w-32'>
              <Controller
                name={`skus.${index}.grossWeight`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Gross weight'
                    isInvalid={!!cardErrors?.grossWeight}
                    validationMessage={
                      cardErrors?.grossWeight?.message as string
                    }
                  />
                )}
              />
            </div>
          </div>
        </FieldZone>
        <PalletisingSection
          skuIndex={index}
          control={control}
          formState={formState}
        />
      </div>
    </section>
  );
}
